import React, { useState, useEffect } from "react";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Tooltip } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { ticketStatus } from "../../constants/ticketStatus";
import DataTable from "../ui/DataTable";
import { colors } from "../../constants/colors";
import supabase from "../../supabase";
import Swal from 'sweetalert2';
import { resendBill } from "../../apiRequests/post";
import { formatCurrencyManual } from "../../utils/formatters";
import { TicketStatusChip } from "../ui";

const baseOptions = {
  responsive: "simple",
  sortOrder: { name: "ticketId", direction: "desc" },
  enableNestedDataAccess: ".",
  rowsPerPage: 25,
  downloadOptions: {
    filename: "facturas.csv",
    separator: ";",
    filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
  },
};

const TicketsTable = ({ 
  tickets, 
  isAdmin, 
  clientId,
  selectedTickets = null,
  setSelectedTickets = null,
  setTotalExpenses = null,
  setTotalExpensesWithIva = null,
  setTotalIva210 = null,
  setTotalIva105 = null
}) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [resending, setResending] = useState(false);

  const handleResendBill = async (ticket) => {
    const result = await Swal.fire({
      title: 'Reenviar factura?',
      text: `Se reenviará ${ticket.ticketType} ${ticket.ticketNumber} al Cliente.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#b91411',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, reenviar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        setResending(true);
        const response = await resendBill(ticket.ticketType, ticket.ticketNumber);
        
        if (response.error === "S") {
          throw new Error(response.errores || "Error al reenviar la factura");
        }
        
        Swal.fire({
          title: '¡Factura reenviada!',
          text: 'La factura ha sido reenviada exitosamente al cliente.',
          icon: 'success',
          confirmButtonColor: '#b91411'
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo reenviar la factura. Intente nuevamente.',
          icon: 'error',
          confirmButtonColor: '#b91411'
        });
      } finally {
        setResending(false);
      }
    }
  };

  // Determinar si el modo de selección está habilitado
  const isSelectionEnabled = setSelectedTickets !== null;

  // Handler para cuando se seleccionan/deseleccionan filas
  const handleRowSelectionChange = (currentRowsSelected, allRowsSelected, rowsSelected) => {
    if (!isSelectionEnabled) return;

    // Obtener los ticketIds seleccionados
    const selectedTicketIds = rowsSelected.map(rowIndex => tickets[rowIndex].ticketId);
    setSelectedTickets(selectedTicketIds);

    // Calcular totales basados en las facturas seleccionadas
    let totalExpenses = 0;
    let totalWithIva = 0;
    let iva21 = 0;
    let iva105 = 0;

    rowsSelected.forEach(rowIndex => {
      const ticket = tickets[rowIndex];
      if (ticket && ticket.totalAmount) {
        totalWithIva += Number(ticket.totalAmount) || 0;
      }

      const orderCurrencyRate = (() => {
        const ro = ticket?.relatedOrders;
        if (!ro || !ro.length) return 1;
        const r = parseFloat(ro[0].currencyRate);
        return !isNaN(r) && r > 0 ? r : 1;
      })();

      if (ticket && ticket.ticketDetails) {
        ticket.ticketDetails.forEach((detail) => {
          const price = Number(detail.price) || 0;
          const quantity = Number(detail.quantity) || 0;
          const ivaPercentage = Number(detail.ivaPercentage) || 0;
          const mult =
            detail.currency === 1 || detail.currency === "1"
              ? 1
              : orderCurrencyRate;
          const subtotal = price * quantity * mult;

          totalExpenses += subtotal;

          if (ivaPercentage === 21) {
            iva21 += subtotal * 0.21;
          } else if (ivaPercentage === 10.5) {
            iva105 += subtotal * 0.105;
          }
        });
      }
    });

    if (setTotalExpenses) setTotalExpenses(totalExpenses);
    if (setTotalExpensesWithIva) setTotalExpensesWithIva(totalWithIva);
    if (setTotalIva210) setTotalIva210(iva21);
    if (setTotalIva105) setTotalIva105(iva105);
  };

  const [search, setSearch] = useState("");

  // Opciones dinámicas basadas en si la selección está habilitada
  const options = {
    ...baseOptions,
    selectableRows: isSelectionEnabled ? "multiple" : "none",
    onRowSelectionChange: isSelectionEnabled ? handleRowSelectionChange : undefined,
    rowsSelected: isSelectionEnabled && selectedTickets 
      ? tickets.map((ticket, index) => selectedTickets.includes(ticket.ticketId) ? index : -1).filter(i => i !== -1)
      : [],
  };

  useEffect(() => {
    const getClients = async () => {
      try {
        const { data: clientsData, error } = await supabase
          .from("clients")
          .select("*");
          
        if (error) throw error;
        setClients(clientsData);
      } catch (error) {
        console.error("Error al obtener clientes:", error);
      }
    };

    getClients();
  }, []);

  // Función para manejar clicks en links de órdenes
  const handleOrderLinkClick = (e, orderData) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/order-details-view", {
      state: {
        orderData: orderData,
        fromTickets: true,
        clientId: clientId, // Asegurar que el clientId esté disponible
      },
    });
  };

  const columns = [
    {
      name: "date",
      label: "Fecha",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "comprobante",
      label: "Comprobante",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const ticket = visibleTickets[tableMeta.rowIndex];
          if (!ticket || !ticket.relatedOrders || ticket.relatedOrders.length === 0) {
            return <div>{value}</div>;
          }

          // Crear los links de órdenes con event handlers directos
          const orderLinks = ticket.relatedOrders.map((order, index) => (
            <React.Fragment key={order.orderId}>
              <a
                href="#"
                onClick={(e) => handleOrderLinkClick(e, order)}
                style={{ color: 'var(--sup-brand-dark)', textDecoration: 'none', fontWeight: 500 }}
              >
                {order.orderId}
              </a>
              {index < ticket.relatedOrders.length - 1 ? ', ' : ''}
            </React.Fragment>
          ));
          
          return (
            <div>
              {value}
              <span> (Órdenes: {orderLinks})</span>
            </div>
          );
        }
      },
    },    
    {
      name: "totalAmount",
      label: "Total iva incluido",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const ticket = visibleTickets[tableMeta.rowIndex];
          const amount = Number(ticket.totalAmount) || 0;
          return formatCurrencyManual(amount);
        }
      },
    },
    {
      name: "status",
      label: "Estado",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const item =
            visibleTickets[tableMeta.currentTableData[tableMeta.rowIndex].index];
          const known = ticketStatus.some((s) => s.value === item?.status);
          if (!known) return "Estado desconocido";
          return <TicketStatusChip status={item.status} />;
        },
      },
    },
    
    {
      name: "acciones",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          const itemTicket = visibleTickets[dataIndex];
          const itemClient = clients.find(client => client.clientId === itemTicket.clientId);
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Pagar Factura">
                <AttachMoneyIcon
                  id={itemTicket.ticketId}
                  style={{
                    cursor: "pointer",
                    color: colors.red,
                    margin: "0 5px",
                  }}
                  onClick={() => {
                    navigate("/register-bill-payment", {
                    state: { client: itemClient, ticket: itemTicket },
                  });
                }}
                />
              </Tooltip>
              <Tooltip title="Reenviar Factura">
                <MailOutlineIcon
                  style={{
                    cursor: resending ? "wait" : "pointer",
                    color: colors.red,
                    margin: "0 5px",
                    opacity: resending ? 0.5 : 1
                  }}
                  onClick={() => !resending && handleResendBill(itemTicket)}
                />
              </Tooltip>
            </div>
          );
        },
      },
    },
  ];

  const term = search.trim().toLowerCase();
  const visibleTickets = term
    ? tickets.filter((ticket) =>
        Object.values(ticket || {}).some(
          (value) =>
            value !== null &&
            typeof value !== "object" &&
            String(value).toLowerCase().includes(term)
        )
      )
    : tickets;

  return (
    <DataTable
      data={visibleTickets}
      columns={columns}
      heading="Facturas"
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Buscar por número, cliente o importe…",
      }}
      resultCount={visibleTickets.length}
      totalCount={tickets.length}
      emptyMessage="No hay facturas para mostrar"
      options={options}
    />
  );
};

export default TicketsTable;
